import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  fetchSignInMethodsForEmail,
  linkWithCredential
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase.js';

export const registerUser = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Initialize user document in Firestore
    await setDoc(doc(db, 'users', userCredential.user.uid), {
      email: userCredential.user.email,
      credits: 0,
      createdAt: new Date()
    });
    
    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/auth/verify-complete`,
        handleCodeInApp: false,
      };
      await sendEmailVerification(userCredential.user, actionCodeSettings);
      console.info('email test here - Verification email requested for', userCredential.user.email);
    } catch (ve) {
      console.error('email test here - Failed to request verification email:', ve);
    }
    return { data: userCredential.user, error: null };
  } catch (error) {
    let errorMessage = 'An error occurred during registration';
    
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'This email is already registered';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address';
        break;
      case 'auth/operation-not-allowed':
        errorMessage = 'Email/password accounts are not enabled';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password should be at least 6 characters';
        break;
    }
    
    return { data: null, error: errorMessage };
  }
};

export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { data: userCredential.user, error: null };
  } catch (error) {
    let errorMessage = 'An error occurred during login';
    
    switch (error.code) {
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled';
        break;
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        errorMessage = 'Invalid email or password';
        break;
    }
    
    return { data: null, error: errorMessage };
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { error: null };
  } catch (error) {
    return { error: 'An error occurred during logout' };
  }
};

export const requestPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { error: null };
  } catch (error) {
    return { error: 'Failed to send password reset email' };
  }
};

export const resendVerificationEmail = async () => {
  const user = auth.currentUser;
  if (!user) return { error: 'Not signed in' };
  try {
    const actionCodeSettings = {
      url: `${window.location.origin}/auth/verify-complete`,
      handleCodeInApp: false,
    };
    await sendEmailVerification(user, actionCodeSettings);
    return { error: null };
  } catch (error) {
    return { error: 'Failed to send verification email' };
  }
};

export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    return { data: result.user, error: null };
  } catch (error) {
    // Handle account-exists-with-different-credential (email already registered with password)
    if (error.code === 'auth/account-exists-with-different-credential') {
      return {
        data: null,
        error: 'account-exists-with-different-credential',
        email: error.customData?.email,
        pendingCredential: error.credential
      };
    }
    
    let errorMessage = 'Failed to sign in with Google';
    switch (error.code) {
      case 'auth/popup-closed-by-user':
        errorMessage = 'Sign-in popup was closed';
        break;
      case 'auth/popup-blocked':
        errorMessage = 'Sign-in popup was blocked by browser';
        break;
      case 'auth/cancelled-popup-request':
        errorMessage = 'Sign-in was cancelled';
        break;
    }
    
    return { data: null, error: errorMessage };
  }
};

export const linkGoogleToExistingAccount = async (email, password, pendingCredential) => {
  try {
    // Sign in with email/password to get the existing user
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Link the Google credential to this user
    await linkWithCredential(userCredential.user, pendingCredential);
    
    return { data: userCredential.user, error: null };
  } catch (error) {
    let errorMessage = 'Failed to link accounts';
    
    switch (error.code) {
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled';
        break;
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        errorMessage = 'Invalid email or password';
        break;
      case 'auth/credential-already-in-use':
        errorMessage = 'This Google account is already linked to another user';
        break;
    }
    
    return { data: null, error: errorMessage };
  }
};

export const initializeAuthListener = (callback) => {
  return onAuthStateChanged(auth, (user) => {
    callback(user);
  });
};

// Protected routes that are safe to redirect to
const PROTECTED_ROUTES = [
  '/library',
  '/create',
  '/design',
  '/account',
  '/admin'
];

/**
 * Validates that a path is a safe, protected route to redirect to
 * Prevents redirect attacks by only allowing whitelisted route prefixes
 * @param {string} path - The path to validate
 * @returns {boolean} - True if path is a valid protected route, false otherwise
 */
export const isValidRedirectRoute = (path) => {
  if (!path) return false;
  // Check if path starts with any protected route
  return PROTECTED_ROUTES.some(route => path.startsWith(route));
};